import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { 
  Loader2, Search, Shield, MessageCircle, Key, 
  Check, X, RefreshCw, Users, Database, Trash2, Calendar, BarChart3, Table2, UserCheck
} from "lucide-react";
import { apiClient } from "../../../App";
import { toast } from "sonner";

const PermissionsTab = ({ password }) => {
  // Users with permission status
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  
  // Tick data management
  const [tickStats, setTickStats] = useState(null);
  const [tickStatsLoading, setTickStatsLoading] = useState(false);
  const [deleteDate, setDeleteDate] = useState('');
  const [deleteTokens, setDeleteTokens] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch users with permission status
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const response = await apiClient.get(
        `/admin/users-telegram-status?admin_password=${encodeURIComponent(password)}&limit=200`
      );
      if (response.data.success) {
        // Also fetch Zerodha permissions for each user
        const usersWithPermissions = await Promise.all(
          response.data.users.map(async (user) => {
            try {
              // We'll use the bulk data from users-telegram-status
              return {
                ...user,
                // Zerodha permission would be fetched separately if needed
                zerodha_enabled: false // Will be determined from backend
              };
            } catch {
              return user;
            }
          })
        );
        setUsers(usersWithPermissions);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to load users');
    }
    setUsersLoading(false);
  }, [password]);

  // Fetch tick stats
  const fetchTickStats = useCallback(async () => {
    setTickStatsLoading(true);
    try {
      const response = await apiClient.get(
        `/admin/tick-stats?admin_password=${encodeURIComponent(password)}`
      );
      if (response.data.success) {
        setTickStats(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch tick stats:', err);
    }
    setTickStatsLoading(false);
  }, [password]);

  // Initial fetch
  useEffect(() => {
    fetchUsers();
    fetchTickStats();
  }, [fetchUsers, fetchTickStats]);

  // Toggle Telegram permission
  const toggleTelegramPermission = async (userId, currentEnabled) => {
    setActionLoading(`telegram-${userId}`);
    try {
      const response = await apiClient.post('/admin/telegram-permission', {
        admin_password: password,
        user_id: userId,
        enabled: !currentEnabled
      });
      if (response.data.success) {
        toast.success(response.data.message);
        // Update local state
        setUsers(users.map(u => 
          u.id === userId 
            ? { ...u, telegram_enabled_by_admin: !currentEnabled }
            : u
        ));
      }
    } catch (err) {
      toast.error('Failed to update Telegram permission');
    }
    setActionLoading(null);
  };

  // Toggle Zerodha Auth permission
  const toggleZerodhaPermission = async (userId, currentEnabled) => {
    setActionLoading(`zerodha-${userId}`);
    try {
      const response = await apiClient.post('/admin/zerodha-auth-permission', {
        admin_password: password,
        user_id: userId,
        enabled: !currentEnabled
      });
      if (response.data.success) {
        toast.success(response.data.message);
        // Update local state
        setUsers(users.map(u => 
          u.id === userId 
            ? { ...u, zerodha_auth_enabled: !currentEnabled }
            : u
        ));
      }
    } catch (err) {
      toast.error('Failed to update Zerodha permission');
    }
    setActionLoading(null);
  };

  // Toggle Referral Dashboard permission
  const toggleReferralDashboardPermission = async (userId, currentEnabled) => {
    setActionLoading(`referral-${userId}`);
    try {
      const response = await apiClient.post('/admin/referral-dashboard-permission', {
        admin_password: password,
        user_id: userId,
        enabled: !currentEnabled
      });
      if (response.data.success) {
        toast.success(response.data.message);
        // Update local state
        setUsers(users.map(u => 
          u.id === userId 
            ? { ...u, referral_dashboard_enabled: !currentEnabled }
            : u
        ));
      }
    } catch (err) {
      toast.error('Failed to update Referral Dashboard permission');
    }
    setActionLoading(null);
  };

  // Toggle Algo Telegram permission
  const toggleAlgoTelegramPermission = async (userId, currentEnabled) => {
    setActionLoading(`algo-${userId}`);
    try {
      const response = await apiClient.post('/admin/algo-telegram-permission', {
        admin_password: password,
        user_id: userId,
        enabled: !currentEnabled
      });
      if (response.data.success) {
        toast.success(response.data.message);
        // Update local state
        setUsers(users.map(u => 
          u.id === userId 
            ? { ...u, algo_telegram_enabled: !currentEnabled }
            : u
        ));
      }
    } catch (err) {
      toast.error('Failed to update Algo Telegram permission');
    }
    setActionLoading(null);
  };

  // Delete tick data
  const handleDeleteTicks = async () => {
    if (!deleteDate) {
      toast.error('Please select a date');
      return;
    }
    
    setDeleteLoading(true);
    try {
      const tokens = deleteTokens 
        ? deleteTokens.split(',').map(t => parseInt(t.trim())).filter(t => !isNaN(t))
        : [];
      
      const response = await apiClient.post('/admin/delete-ticks', {
        admin_password: password,
        date: deleteDate,
        tokens: tokens.length > 0 ? tokens : undefined
      });
      
      if (response.data.success) {
        toast.success(`Deleted ${response.data.deleted_count} tick records`);
        setShowDeleteConfirm(false);
        setDeleteDate('');
        setDeleteTokens('');
        fetchTickStats(); // Refresh stats
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete tick data');
    }
    setDeleteLoading(false);
  };

  // Filter users by search
  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Toggle admin approval for user
  const toggleAdminApproval = async (userEmail, currentApproved) => {
    setActionLoading(`approve_${userEmail}`);
    try {
      const response = await apiClient.post('/admin/approve-user', {
        admin_password: password,
        user_email: userEmail,
        approved: !currentApproved
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Update local state
        setUsers(prev => prev.map(u => 
          u.email === userEmail 
            ? { ...u, admin_approved: !currentApproved }
            : u
        ));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update approval status');
    }
    setActionLoading(null);
  };

  // Set subscription duration for user
  const setSubscriptionDays = async (userEmail, days, plan = 'admin_granted') => {
    setActionLoading(`sub_${userEmail}_${days}`);
    try {
      const response = await apiClient.post('/admin/set-subscription', {
        admin_password: password,
        user_email: userEmail,
        days: days,
        plan: plan
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Update local state
        setUsers(prev => prev.map(u => 
          u.email === userEmail 
            ? { 
                ...u, 
                subscription_status: 'active',
                subscription_plan: plan,
                subscription_end: response.data.subscription_end,
                days_remaining: days,
                admin_approved: true
              }
            : u
        ));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set subscription');
    }
    setActionLoading(null);
  };

  // State for expanded user card (to show subscription options)
  const [expandedUser, setExpandedUser] = useState(null);

  return (
    <div className="space-y-6">
      {/* Tick Data Management Section */}
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-red-400 flex items-center gap-2">
            <Database className="w-4 h-4" />
            Tick Data Management
          </h3>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchTickStats}
            disabled={tickStatsLoading}
            className="text-xs"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${tickStatsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        {/* Tick Stats */}
        {tickStats && (
          <div className="mb-4 p-3 bg-[#0a0e17] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#6b7a99]">Total Ticks:</span>
              <span className="text-sm font-semibold text-white">
                {tickStats.total_ticks?.toLocaleString() || 0}
              </span>
            </div>
            {tickStats.by_date && tickStats.by_date.length > 0 && (
              <div className="space-y-1 max-h-32 overflow-y-auto">
                <p className="text-[10px] text-[#6b7a99] mb-1">By Date:</p>
                {tickStats.by_date.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="text-[#6b7a99]">{item.date}</span>
                    <span className="text-white">
                      {item.tick_count?.toLocaleString()} ticks ({item.symbol_count} symbols)
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Delete Controls */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-[#6b7a99] mb-1 block">Date to Delete</label>
              <Input
                type="date"
                value={deleteDate}
                onChange={(e) => setDeleteDate(e.target.value)}
                className="bg-[#0a0e17] border-[#1a2540] text-white text-xs h-8"
              />
            </div>
            <div>
              <label className="text-[10px] text-[#6b7a99] mb-1 block">Token IDs (optional, comma-separated)</label>
              <Input
                type="text"
                value={deleteTokens}
                onChange={(e) => setDeleteTokens(e.target.value)}
                placeholder="e.g., 256265, 260105"
                className="bg-[#0a0e17] border-[#1a2540] text-white text-xs h-8"
              />
            </div>
          </div>
          
          {!showDeleteConfirm ? (
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={!deleteDate}
              variant="destructive"
              size="sm"
              className="w-full text-xs"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete Tick Data
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                onClick={handleDeleteTicks}
                disabled={deleteLoading}
                variant="destructive"
                size="sm"
                className="flex-1 text-xs"
              >
                {deleteLoading ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Check className="w-3 h-3 mr-1" />
                )}
                Confirm Delete
              </Button>
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* User Permissions Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Shield className="w-4 h-4 text-cyan-400" />
            User Permissions
          </h3>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchUsers}
            disabled={usersLoading}
            className="text-xs"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${usersLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users by email or name..."
            className="pl-10 bg-[#0a0e17] border-[#1a2540] text-white text-xs h-9"
          />
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-3 text-[10px]">
          <div className="flex items-center gap-1">
            <UserCheck className="w-3 h-3 text-emerald-400" />
            <span className="text-[#6b7a99]">Approved</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3 text-cyan-400" />
            <span className="text-[#6b7a99]">Set Days</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageCircle className="w-3 h-3 text-blue-400" />
            <span className="text-[#6b7a99]">Telegram</span>
          </div>
          <div className="flex items-center gap-1">
            <Key className="w-3 h-3 text-purple-400" />
            <span className="text-[#6b7a99]">Zerodha</span>
          </div>
          <div className="flex items-center gap-1">
            <BarChart3 className="w-3 h-3 text-green-400" />
            <span className="text-[#6b7a99]">Referral</span>
          </div>
          <div className="flex items-center gap-1">
            <Table2 className="w-3 h-3 text-cyan-400" />
            <span className="text-[#6b7a99]">Algo TG</span>
          </div>
        </div>

        {/* Users List */}
        {usersLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <p className="text-center text-[#6b7a99] text-xs py-4">
            {searchQuery ? 'No users found matching search' : 'No users found'}
          </p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {filteredUsers.map((user) => (
              <div 
                key={user.id}
                className="p-3 bg-[#111827] border border-[#1a2540] rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {user.name || 'Unnamed User'}
                    </p>
                    <p className="text-[10px] text-[#6b7a99] truncate">
                      {user.email}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                        user.subscription_status === 'active' 
                          ? 'bg-green-500/20 text-green-400'
                          : user.subscription_status === 'trial'
                          ? 'bg-blue-500/20 text-blue-400'
                          : user.subscription_status === 'expired'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {user.subscription_status?.toUpperCase() || 'FREE'}
                      </span>
                      {/* Days Remaining */}
                      {user.days_remaining !== null && user.days_remaining !== undefined && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                          user.days_remaining <= 0 
                            ? 'bg-red-500/20 text-red-400'
                            : user.days_remaining <= 3 
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-cyan-500/20 text-cyan-400'
                        }`}>
                          {user.days_remaining <= 0 ? 'EXPIRED' : `${user.days_remaining}d left`}
                        </span>
                      )}
                      {/* Admin Approval Status */}
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                        user.admin_approved !== false
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {user.admin_approved !== false ? 'APPROVED' : 'PENDING'}
                      </span>
                      {user.telegram_configured && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                          TG
                        </span>
                      )}
                    </div>
                    
                    {/* Subscription Duration Buttons - Always visible */}
                    <div className="flex items-center gap-1 mt-2">
                      <span className="text-[9px] text-[#6b7a99] mr-1">Set Days:</span>
                      {[1, 3, 7, 15, 30, 90, 180, 365].map(days => (
                        <button
                          key={days}
                          onClick={() => setSubscriptionDays(user.email, days)}
                          disabled={actionLoading?.startsWith(`sub_${user.email}`)}
                          className={`px-1.5 py-0.5 text-[8px] rounded border transition-colors ${
                            user.days_remaining === days
                              ? 'bg-cyan-500/30 border-cyan-500/50 text-cyan-300'
                              : 'bg-[#1a2540] border-[#2a3a5a] text-[#8a9ab0] hover:bg-cyan-500/20 hover:border-cyan-500/40 hover:text-cyan-400'
                          }`}
                        >
                          {actionLoading === `sub_${user.email}_${days}` ? (
                            <Loader2 className="w-2 h-2 animate-spin" />
                          ) : (
                            days === 365 ? '1Y' : days >= 30 ? `${Math.floor(days/30)}M` : `${days}d`
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Permission Toggles */}
                  <div className="flex items-center gap-1.5 ml-2">
                    {/* Admin Approval Toggle - First/Most Important */}
                    <button
                      onClick={() => toggleAdminApproval(user.email, user.admin_approved !== false)}
                      disabled={actionLoading === `approve_${user.email}`}
                      className={`p-1.5 rounded transition-colors ${
                        user.admin_approved !== false
                          ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                          : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                      }`}
                      title={user.admin_approved !== false ? 'Revoke Access' : 'Approve Access'}
                    >
                      {actionLoading === `approve_${user.email}` ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <UserCheck className="w-3.5 h-3.5" />
                      )}
                    </button>

                    {/* Telegram Permission Toggle */}
                    <button
                      onClick={() => toggleTelegramPermission(user.id, user.telegram_enabled_by_admin)}
                      disabled={actionLoading === `telegram-${user.id}`}
                      className={`p-1.5 rounded-lg transition-all ${
                        user.telegram_enabled_by_admin
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                          : 'bg-[#1a2540] text-[#6b7a99] border border-[#1a2540] hover:border-blue-500/40'
                      }`}
                      title={`Telegram Alerts: ${user.telegram_enabled_by_admin ? 'Enabled' : 'Disabled'}`}
                    >
                      {actionLoading === `telegram-${user.id}` ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <MessageCircle className="w-3.5 h-3.5" />
                      )}
                    </button>
                    
                    {/* Zerodha Auth Permission Toggle */}
                    <button
                      onClick={() => toggleZerodhaPermission(user.id, user.zerodha_auth_enabled)}
                      disabled={actionLoading === `zerodha-${user.id}`}
                      className={`p-1.5 rounded-lg transition-all ${
                        user.zerodha_auth_enabled
                          ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40'
                          : 'bg-[#1a2540] text-[#6b7a99] border border-[#1a2540] hover:border-purple-500/40'
                      }`}
                      title={`Zerodha Auth: ${user.zerodha_auth_enabled ? 'Enabled' : 'Disabled'}`}
                    >
                      {actionLoading === `zerodha-${user.id}` ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Key className="w-3.5 h-3.5" />
                      )}
                    </button>
                    
                    {/* Referral Dashboard Permission Toggle */}
                    <button
                      onClick={() => toggleReferralDashboardPermission(user.id, user.referral_dashboard_enabled)}
                      disabled={actionLoading === `referral-${user.id}`}
                      className={`p-1.5 rounded-lg transition-all ${
                        user.referral_dashboard_enabled
                          ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                          : 'bg-[#1a2540] text-[#6b7a99] border border-[#1a2540] hover:border-green-500/40'
                      }`}
                      title={`Referral Dashboard: ${user.referral_dashboard_enabled ? 'Enabled' : 'Disabled'}${user.referral_count > 0 ? ` (${user.referral_count} referrals)` : ''}`}
                    >
                      {actionLoading === `referral-${user.id}` ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <BarChart3 className="w-3.5 h-3.5" />
                      )}
                    </button>
                    
                    {/* Algo Telegram Permission Toggle */}
                    <button
                      onClick={() => toggleAlgoTelegramPermission(user.id, user.algo_telegram_enabled)}
                      disabled={actionLoading === `algo-${user.id}`}
                      className={`p-1.5 rounded-lg transition-all ${
                        user.algo_telegram_enabled
                          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40'
                          : 'bg-[#1a2540] text-[#6b7a99] border border-[#1a2540] hover:border-cyan-500/40'
                      }`}
                      title={`Algo Telegram Alerts: ${user.algo_telegram_enabled ? 'Enabled' : 'Disabled'}`}
                    >
                      {actionLoading === `algo-${user.id}` ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Table2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>
                {/* Show referral stats if user has referrals */}
                {user.referral_count > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#1a2540]">
                    <span className="text-[9px] text-green-400">
                      {user.referral_count} referral{user.referral_count > 1 ? 's' : ''} | Code: {user.referral_code}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Total count */}
        <p className="text-[10px] text-[#6b7a99] mt-2 text-right">
          Showing {filteredUsers.length} of {users.length} users
        </p>
      </div>
    </div>
  );
};

export default PermissionsTab;
